'use client';

/**
 * IntentAddScheduleCard — explorer-surface "Add Schedule" affordance.
 *
 * Fires `setExplorerSelection('add_schedule')` so mode3's
 * `selection_presets["add_schedule"]` activates the
 * `mode3_add_schedule` preset (full-bleed `intent_pdf_ingest` card).
 * The canvas swaps via the matrix's cards-everywhere dispatch, not
 * via the global tab system.
 *
 * Why a fresh component (not a wrapper of `IntentAddScheduleSection`):
 * the legacy section calls `openTab({type: 'add_schedule'})` which
 * routes the form through the tab-system overlay. New modes
 * shouldn't carry tab-system debt — when the tab system retires,
 * mode3 would break. Cards-everywhere primitive is
 * `setExplorerSelection`, which feeds the matrix routing.
 *
 * Visual chrome mirrors the legacy section so the cohabitating
 * legacy `intent` mode and mode3 explorer cards look identical to
 * the user.
 *
 * @see klyde_edge/composition/cards/intent.py — content_key="intent_add_schedule"
 *
 * @component_role: Card
 * @component_type: Explorer Card
 * @domain: Explorer / Intent
 * @layer: presentation
 */

import { FileUp } from 'lucide-react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useExplorerLabels } from '@/lib/stores/total-matrix-store';
import { cn } from '@/lib/utils/cn';

export function IntentAddScheduleCard() {
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const labels = useExplorerLabels('intent');
  const label = labels.add_schedule_label ?? 'Add Schedule';

  return (
    <div className="px-3 py-2">
      <button
        type="button"
        onClick={() => setExplorerSelection('add_schedule')}
        className={cn(
          'w-full py-2 px-3 rounded-md text-sm',
          'flex items-center justify-center gap-2',
          'border-2 border-dashed border-border',
          'text-muted-foreground hover:text-foreground hover:border-primary/50',
          'transition-colors',
        )}
      >
        <FileUp className="w-4 h-4" />
        {label}
      </button>
    </div>
  );
}
